import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Search } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { toast } from 'sonner';
import { RefundDowngradeButton } from '@/components/admin/RefundDowngradeButton';
import { formatDate } from '@/lib/utils';

interface Subscription {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  planName: string;
  status: string;
  stripeSubscriptionId: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  amountCents: number;
  currency: string;
  lastPaymentDate?: string;
  createdAt: string;
}

export function SubscriptionManagement() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await authFetch('/admin/subscriptions/active');
      if (!response.ok) throw new Error('Failed to fetch subscriptions');
      
      const data = await response.json();
      setSubscriptions(data);
    } catch (error) {
      toast.error('Failed to load subscriptions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleRefundComplete = () => {
    // Refresh the list after a successful refund
    handleRefresh();
  };

  const filteredSubscriptions = subscriptions.filter(sub => 
    sub.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.planName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatAmount = (amountCents: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amountCents / 100);
  };

  const getStatusBadge = (status: string, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd) {
      return <Badge variant="secondary">Canceling</Badge>;
    }
    
    switch (status) {
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'past_due':
        return <Badge variant="destructive">Past Due</Badge>;
      case 'unpaid':
        return <Badge variant="destructive">Unpaid</Badge>;
      case 'canceled':
        return <Badge variant="secondary">Canceled</Badge>;
      case 'incomplete':
        return <Badge variant="secondary">Incomplete</Badge>;
      case 'incomplete_expired':
        return <Badge variant="secondary">Expired</Badge>;
      case 'trialing':
        return <Badge variant="outline">Trial</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Subscription Management</h1>
          <p className="text-gray-600 mt-2">Manage active paid subscriptions and process refunds</p>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active Paid Subscriptions</CardTitle>
          <CardDescription>
            Total: {subscriptions.length} active subscriptions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by email, name, or plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Current Period</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading subscriptions...
                    </TableCell>
                  </TableRow>
                ) : filteredSubscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      No active subscriptions found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubscriptions.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{subscription.userName}</div>
                          <div className="text-sm text-gray-600">{subscription.userEmail}</div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{subscription.planName}</TableCell>
                      <TableCell>
                        {formatAmount(subscription.amountCents, subscription.currency)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(subscription.status, subscription.cancelAtPeriodEnd)}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>Start: {formatDate(subscription.currentPeriodStart)}</div>
                          <div>End: {formatDate(subscription.currentPeriodEnd)}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {formatDate(subscription.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <RefundDowngradeButton
                          userId={subscription.userId}
                          userEmail={subscription.userEmail}
                          onComplete={handleRefundComplete}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}